import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { usePlayer } from "../context/PlayerContext";
import { Avatar, CreatorName, formatNum, Loading, VScore } from "../components/common";
import { CrewEmblem } from "./CrewsDiscover";
import { BarChart3, Play, Shield, Star } from "lucide-react";

export default function Charts() {
  const [mode, setMode] = useState("creators");
  const [meta, setMeta] = useState(null);
  const [category, setCategory] = useState("Top Artists");
  const [period, setPeriod] = useState("This Week");
  const [world, setWorld] = useState("Global");
  const [data, setData] = useState(null);
  const player = usePlayer();

  useEffect(() => {
    if (mode === "creators") { api.get("/chart-categories").then(({ data }) => setMeta(data)); setCategory("Top Artists"); }
    else { api.get("/crews/chart-categories").then(({ data }) => setMeta(data)); setCategory("Top Crews"); }
  }, [mode]);
  useEffect(() => {
    setData(null);
    const url = mode === "creators" ? "/charts" : "/crews/charts";
    api.get(`${url}?category=${encodeURIComponent(category)}&period=${encodeURIComponent(period)}&world=${encodeURIComponent(world)}`).then(({ data }) => setData(data));
  }, [category, period, world, mode]);

  const mv = (m) => m.startsWith("▲") ? "text-emerald-400" : m.startsWith("▼") ? "text-rose-400" : m === "NEW" ? "text-eblue" : "text-muted-foreground";

  return (
    <div className="px-4 md:px-6 py-5">
      <div className="flex items-center gap-2 mb-4"><BarChart3 className="text-gold" /><h1 className="font-display font-bold text-2xl">VCharts</h1></div>
      <div className="flex items-center gap-1 surface-2 rounded-full p-1 w-fit mb-4">
        <button onClick={() => { setMode("creators"); setData(null); }} className={`text-sm px-4 py-1.5 rounded-full flex items-center gap-1.5 ${mode === "creators" ? "bg-white text-black font-semibold" : "text-muted-foreground"}`} data-testid="charts-mode-creators"><Star size={14} />Creators</button>
        <button onClick={() => { setMode("crews"); setData(null); }} className={`text-sm px-4 py-1.5 rounded-full flex items-center gap-1.5 ${mode === "crews" ? "bg-gold text-black font-semibold" : "text-muted-foreground"}`} data-testid="charts-mode-crews"><Shield size={14} />Crews</button>
      </div>
      {meta && (
        <div className="space-y-3 mb-6">
          <div className="flex gap-2 overflow-x-auto hide-scroll pb-1">
            {meta.categories.map((c) => <button key={c} onClick={() => setCategory(c)} data-testid={`chart-cat-${c.replace(/\s/g, "")}`} className={`px-3.5 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors ${category === c ? "bg-white text-black" : "surface-2 text-muted-foreground hover:text-white"}`}>{c}</button>)}
          </div>
          <div className="flex flex-wrap gap-2">
            {(meta.periods || ["Today", "This Week", "This Month", "This Year", "All Time"]).map((p) => <button key={p} onClick={() => setPeriod(p)} className={`px-3 py-1 rounded-full text-xs border transition-colors ${period === p ? "border-eblue text-eblue" : "border-white/10 text-muted-foreground"}`}>{p}</button>)}
            <span className="w-px bg-white/10 mx-1" />
            {meta.worlds.map((w) => <button key={w} onClick={() => setWorld(w)} className={`px-3 py-1 rounded-full text-xs border transition-colors ${world === w ? "border-gold text-gold" : "border-white/10 text-muted-foreground"}`}>{w}</button>)}
          </div>
        </div>
      )}

      {!data ? <Loading /> : data.entries.length === 0 ? <div className="text-center text-muted-foreground py-16">No chart data for this selection yet.</div> : mode === "crews" ? (
        <div className="surface rounded-2xl divide-y divide-white/5 overflow-hidden" data-testid="crew-charts-list">
          {data.entries.map((c) => (
            <Link key={c.id} to={`/crew/@${c.handle}`} className="flex items-center gap-4 p-3 hover:bg-white/5 transition-colors" data-testid={`crew-chart-row-${c.rank}`}>
              <div className="w-8 text-center font-mono text-lg text-gold">{c.rank}</div>
              <CrewEmblem crew={c} size={48} />
              <div className="min-w-0 flex-1"><div className="font-semibold text-sm truncate">{c.name}</div><div className="text-xs text-muted-foreground truncate">@{c.handle} · {c.member_count} members · {c.primary_world}</div></div>
              <div className="text-right text-sm font-mono text-gold">{formatNum(c.score)}<div className="text-[10px] text-muted-foreground">crew score</div></div>
            </Link>
          ))}
        </div>
      ) : (
        <div className="surface rounded-2xl divide-y divide-white/5 overflow-hidden">
          {data.entries.filter((row) => row.entity).map((row) => {
            const e = row.entity;
            const isUser = row.type === "user";
            const isTrack = row.type === "track";
            return (
              <div key={`${row.type}-${row.rank}`} className="flex items-center gap-4 p-3 hover:bg-white/5 transition-colors group" data-testid={`chart-row-${row.rank}`}>
                <div className="w-8 text-center"><span className="font-mono text-lg text-gold">{row.rank}</span><div className={`text-[10px] font-mono ${mv(row.movement)}`}>{row.movement}</div></div>
                {isTrack ? (
                  <button onClick={() => player.playTrack(e, data.entries.filter(r => r.type === "track").map(r => r.entity))} className="relative shrink-0">
                    <img src={e.cover_url} className="w-12 h-12 rounded-lg object-cover" alt="" />
                    <span className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 rounded-lg"><Play size={16} className="fill-white" /></span>
                  </button>
                ) : isUser ? <Link to={`/${e.username}`}><Avatar src={e.avatar_url} name={e.display_name} size={48} /></Link>
                  : <img src={e.thumbnail_url || e.video_url} className="w-16 h-12 rounded-lg object-cover" alt="" />}
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-sm truncate">{isUser ? <Link to={`/${e.username}`} className="hover:text-eblue"><CreatorName user={e} /></Link> : e.title}</div>
                  <div className="text-xs text-muted-foreground truncate">{isTrack ? e.artist?.display_name : isUser ? `@${e.username} · ${e.worlds?.[0] || "—"}` : e.channel?.display_name}</div>
                </div>
                {isUser && <div className="text-right"><VScore value={row.score} /><div className="text-[10px] text-muted-foreground">VStar</div></div>}
                {!isUser && <div className="text-right text-sm font-mono text-silver">{formatNum(row.score)}<div className="text-[10px] text-muted-foreground">{isTrack ? "streams" : "views"}</div></div>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
